import React, { FC } from "react";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { InfoField } from "@/components/pages/chats/component/ui/InfoField";
import { useTranslation } from "react-i18next";
import { useBookingCheckin } from "@/components/pages/chats/hooks/useBookingCheckin.hooks";
import { DocumentType } from "@/types/document";
import { Reservation } from "@/types/reservation";
import { formatDateTimeCompact } from "@/utils/dateUtils";

interface CheckinBookingInfoProps {
  booking: Reservation;
}

const EMPTY_VALUE = "-";

const CIPHERTEXT_PATTERN = /^[0-9a-f]{20,}$/i;

const readableContact = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed || CIPHERTEXT_PATTERN.test(trimmed)) return undefined;

  return trimmed;
};

const formatCheckinMethod = (method?: string | null): string => {
  if (!method) return EMPTY_VALUE;

  return method.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export const CheckinBookingInfo: FC<CheckinBookingInfoProps> = ({
  booking,
}) => {
  const { t } = useTranslation();
  const bookingId = booking.id;
  const { data: checkin, isLoading } = useBookingCheckin(bookingId);

  if (!bookingId || (!isLoading && !checkin)) {
    return null;
  }

  const documents = booking.guest?.documents ?? [];
  const idAndPassportsCount = documents.filter(
    (doc) => doc.type !== DocumentType.OTHER,
  ).length;
  const contractsReceived = documents.filter(
    (doc) => doc.type === DocumentType.OTHER,
  ).length;

  const guestProfile = booking.guest?.profile;

  const sortedCustomQuestionAnswers = [
    ...(booking.customQuestionAnswers || []),
  ].sort((a, b) => a.order - b.order);
  const customQuestionRows =
    sortedCustomQuestionAnswers.length > 0
      ? sortedCustomQuestionAnswers.map((item) => (
          <InfoField
            key={item.id}
            copyable={false}
            title={item.question}
            value={item.answer || EMPTY_VALUE}
          />
        ))
      : [
          <InfoField
            key="checkinCustomQuestion"
            copyable={false}
            title={t("chats.checkinCustomQuestion")}
            value={EMPTY_VALUE}
          />,
        ];

  return (
    <FoldableContainer
      titleNode={t("chats.checkinInfo")}
      extraNode={checkin?.step === "COMPLETED" && <span>Done</span>}
    >
      {isLoading ? (
        <div className="p-4 text-center">{t("common.loading")}</div>
      ) : (
        [
          {
            title: t("chats.checkinInfoStatus"),
            value: checkin?.step?.toLowerCase(),
            copyable: false,
          },
          {
            title: t("chats.checkinDocs"),
            value: `${idAndPassportsCount} ${t("common.received")}`,
            copyable: false,
          },
          {
            title: t("common.contract"),
            value: `${contractsReceived} ${t("common.received")}`,
            copyable: false,
          },
          {
            title: t("common.guests"),
            value: `${booking.preCheckinEnteredGuests ?? 0} ${t("chats.checkinAdults")}`,
            copyable: false,
          },
          {
            title: t("common.phone"),
            value:
              readableContact(guestProfile?.phone) ??
              readableContact(checkin?.phone) ??
              EMPTY_VALUE,
            copyable: true,
          },
          {
            title: t("common.email"),
            value:
              readableContact(guestProfile?.email) ??
              readableContact(checkin?.email) ??
              EMPTY_VALUE,
            copyable: true,
          },
          {
            title: t("chats.checkinMethod"),
            value: formatCheckinMethod(booking.checkinMethod),
            copyable: false,
          },
          {
            title: t("chats.checkinEstimatedTime"),
            value: booking.estimatedTimeOfArrival
              ? formatDateTimeCompact(booking.estimatedTimeOfArrival)
              : EMPTY_VALUE,
            copyable: false,
          },
          {
            title: t("chats.arrivalBy"),
            value: EMPTY_VALUE,
            copyable: false,
          },
        ]
          .map(({ title, value, copyable }) => (
            <InfoField
              key={title}
              copyable={copyable}
              title={title}
              value={value ?? ""}
            />
          ))
          .concat(customQuestionRows)
      )}
    </FoldableContainer>
  );
};
